import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RolesActions } from './roles.actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { ToastService } from '../../core/services/toast/toast.service';
import { RoleService } from '../../core/services/api/role/role.service';


@Injectable()
export class RolesEffects {
  private actions$ = inject(Actions);
  private service = inject(RoleService);
  private toast = inject(ToastService);

  // === FETCH ALL ===
  fetchAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.fetchAll),
      mergeMap(() =>
        this.service.getAll().pipe(
          map((response) => RolesActions.fetchAllSuccess({ items: response.data })),
          catchError((error) => of(RolesActions.fetchAllFailure({ error })))
        )
      )
    )
  );

  // === FETCH PAGINATED ===
  fetchPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.fetchPaginated),
       mergeMap(({ page, limit, search, sort, dateFrom, dateTo }) =>
        this.service.getPaginated({
          page,
          limit,
          search,
          sort,
          dateFrom,
          dateTo,
        }).pipe(
          map((response) =>
            RolesActions.fetchPaginatedSuccess({
              items: response.data.list,
              pagination: response.data.pagination,
            })
          ),
          catchError((error) => of(RolesActions.fetchPaginatedFailure({ error })))
        )
      )
    )
  );

  // === TOGGLE STATUS ===
  toggleStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.toggleStatus),
      mergeMap(({ id }) =>
        this.service.changeStatus(id, { isActive: true }).pipe(
          map((response) => RolesActions.toggleStatusSuccess({ Role: response.data })),
          catchError((error) => of(RolesActions.toggleStatusFailure({ error })))
        )
      )
    )
  );

  // === CREATE ===
  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolesActions.create),
      mergeMap(({ Role }) =>
        this.service.create(Role).pipe(
          map((response) => RolesActions.createSuccess({ Role: response.data })),
          catchError((error) => of(RolesActions.createFailure({ error })))
        )
      )
    )
  );

  // === TOASTS ===
  showSuccessToasts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          RolesActions.createSuccess,
          RolesActions.updateSuccess,
          RolesActions.toggleStatusSuccess
        ),
        tap((action) => {
          if ('Role' in action) {
            const p = action.Role;

            switch (action.type) {
              case RolesActions.toggleStatusSuccess.type: {
                const stateText = p.isActive ? 'activé' : 'désactivé';
                this.toast.success(`Produit "${p.name}" ${stateText} avec succès`);
                break;
              }

              case RolesActions.createSuccess.type: {
                this.toast.success(`Produit "${p.name}" créé avec succès`);
                break;
              }

              case RolesActions.updateSuccess.type: {
                this.toast.success(`Produit "${p.name}" mis à jour avec succès`);
                break;
              }
            }
          }
        })
      ),
    { dispatch: false }
  );

  showErrorToasts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          RolesActions.createFailure,
          RolesActions.updateFailure,
          RolesActions.toggleStatusFailure
        ),
        tap(({ error }) => {
          const msg = error.message || 'Une erreur est survenue';
          this.toast.error(msg);
        })
      ),
    { dispatch: false }
  );
}
